import React, { useState, useCallback, useMemo, ReactNode } from 'react';
import { auditApi } from '@/modules/audit/api/auditApi';
import {
  clearImpersonation,
  readImpersonation,
  writeImpersonation,
  type ImpersonatedPractice,
} from '@/shared/api/impersonationStorage';
import { ImpersonationContext } from './useImpersonation';

export type { ImpersonatedPractice };

export function ImpersonationProvider({ children }: { children: ReactNode }) {
  // Per tab (see impersonationStorage): another tab opening a practice never changes this one.
  const [impersonatedPractice, setImpersonatedPractice] = useState<ImpersonatedPractice | null>(
    readImpersonation,
  );

  const startImpersonation = useCallback((practice: ImpersonatedPractice) => {
    writeImpersonation(practice);
    setImpersonatedPractice(practice);
    // An agency user acting inside a client's account is the event the audit
    // log most needs to show, so record it at the moment it starts rather than
    // inferring it from the practice context on later page views.
    auditApi.recordImpersonation(practice.id, practice.name);
  }, []);

  const stopImpersonation = useCallback(() => {
    clearImpersonation();
    setImpersonatedPractice(null);
  }, []);

  // The name is a snapshot taken when the practice was opened; renaming the practice in Settings
  // updates it here so the banner and top bar follow. Not a new impersonation, so no audit entry.
  const renameImpersonatedPractice = useCallback((id: string, name: string) => {
    setImpersonatedPractice((prev) => {
      if (!prev || prev.id !== id || prev.name === name) return prev;
      const next = { id, name };
      writeImpersonation(next);
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({
      impersonatedPractice,
      startImpersonation,
      stopImpersonation,
      renameImpersonatedPractice,
    }),
    [impersonatedPractice, startImpersonation, stopImpersonation, renameImpersonatedPractice],
  );

  return <ImpersonationContext.Provider value={value}>{children}</ImpersonationContext.Provider>;
}
